/**
 * Sealed storage for AI provider API keys.
 * Prefer IndexedDB; localStorage is migration-only and cleared after seal.
 */

import { openDatabase, STORE } from '@/lib/db';
import { isSealed, isVaultUnlocked, openString, sealString } from './vault';
import * as storage from '@/lib/workspace/storage';

const SECRETS_KEY = 'provider-keys';
/** Legacy — read for migration, never write once IDB holds a sealed blob. */
const LEGACY_LS_KEY = 'opendork:provider-secrets';

/** Map providerId → apiKey (plaintext in memory only). */
export type ProviderSecrets = Record<string, string>;

async function readFromIdb(): Promise<string | null> {
  const db = await openDatabase();
  if (!db) return null;
  const row = await db.get(STORE.SETTINGS, SECRETS_KEY);
  return typeof row === 'string' ? row : null;
}

async function writeToIdb(value: string): Promise<boolean> {
  try {
    const db = await openDatabase();
    if (!db) return false;
    await db.put(STORE.SETTINGS, value as unknown as never, SECRETS_KEY);
    return true;
  } catch {
    return false;
  }
}

async function clearLegacyLs(): Promise<void> {
  try {
    localStorage.removeItem(LEGACY_LS_KEY);
  } catch {
    // ignore
  }
}

async function readRaw(): Promise<string | null> {
  const idb = await readFromIdb();
  if (idb) return idb;
  return localStorage.getItem(LEGACY_LS_KEY);
}

/**
 * Write secrets blob. Prefer IDB; only fall back to LS if IDB unavailable.
 * When writing a sealed blob to IDB, wipe legacy LS immediately.
 */
async function writeRaw(value: string): Promise<void> {
  const ok = await writeToIdb(value);
  if (ok) {
    // Never leave a second copy in LS once IDB is the source of truth
    await clearLegacyLs();
    return;
  }
  // IDB unavailable — last resort (incognito quirks, etc.)
  localStorage.setItem(LEGACY_LS_KEY, value);
}

/** True when workspace settings include a vault (keys must stay sealed). */
async function vaultConfigured(): Promise<boolean> {
  try {
    const settings = await storage.getSettings();
    return Boolean(settings.vault);
  } catch {
    return false;
  }
}

export async function loadProviderSecrets(): Promise<ProviderSecrets> {
  const raw = await readRaw();
  if (!raw) return {};

  try {
    let json = raw;
    if (isSealed(raw)) {
      if (!isVaultUnlocked()) return {};
      json = await openString(raw);
    }
    const parsed = JSON.parse(json) as unknown;
    if (typeof parsed !== 'object' || parsed === null) return {};
    const out: ProviderSecrets = {};
    for (const [k, v] of Object.entries(parsed)) {
      if (typeof v === 'string' && v) out[k] = v;
    }

    // Migrate legacy LS → IDB when we successfully loaded from LS
    const idb = await readFromIdb();
    if (!idb && localStorage.getItem(LEGACY_LS_KEY)) {
      await writeRaw(raw);
    }

    return out;
  } catch {
    return {};
  }
}

export async function saveProviderSecrets(secrets: ProviderSecrets): Promise<void> {
  const json = JSON.stringify(secrets);

  if (isVaultUnlocked()) {
    await writeRaw(await sealString(json));
    return;
  }

  // Vault configured but locked: never overwrite sealed blob with plaintext
  const existing = await readRaw();
  if (existing && isSealed(existing)) {
    throw new Error(
      'Vault is locked — unlock local encryption before saving API keys',
    );
  }

  if (await vaultConfigured()) {
    throw new Error(
      'Unlock local encryption before saving API keys (vault is configured)',
    );
  }

  // Vault off: plain storage (UI requires explicit confirmation)
  await writeRaw(json);
}

export async function setProviderSecret(providerId: string, apiKey: string): Promise<void> {
  const secrets = await loadProviderSecrets();
  if (apiKey) secrets[providerId] = apiKey;
  else delete secrets[providerId];
  await saveProviderSecrets(secrets);
}

export async function removeProviderSecret(providerId: string): Promise<void> {
  const secrets = await loadProviderSecrets();
  delete secrets[providerId];
  await saveProviderSecrets(secrets);
}

/**
 * Re-seal (or unseal) secrets to match current vault state.
 * Must be called while unlocked when converting sealed ↔ plain.
 * No-op if vault is locked and existing blob is sealed (avoids wipe).
 */
export async function resealProviderSecrets(): Promise<void> {
  if (!isVaultUnlocked()) {
    const raw = await readRaw();
    if (raw && isSealed(raw)) return;
  }
  const secrets = await loadProviderSecrets();
  await saveProviderSecrets(secrets);
}

/** Wipe in-memory-facing storage on logout (best-effort). */
export async function clearProviderSecretsStorage(): Promise<void> {
  await clearLegacyLs();
  try {
    const db = await openDatabase();
    if (db) await db.delete(STORE.SETTINGS, SECRETS_KEY);
  } catch {
    // ignore
  }
}
